/**
 * Phone-number helpers for transferring a call to an outside number.
 *
 * Only shape is checked here. Which numbers may actually be dialled (US and
 * Canada, no premium rate, no foreign +1 countries) is the calling service's
 * decision, and its refusal names the rule.
 */

/** A US/Canadian number in E.164, the value `PhoneInput` emits. */
export const isNanpE164 = (value: string): boolean => /^\+1\d{10}$/.test(value);

/** True when the search text is a phone number rather than a name. */
export function looksLikePhoneNumber(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed || /[^\d+\-(). ]/.test(trimmed)) return false;
  return trimmed.replace(/\D/g, '').length >= 10;
}

/**
 * A full US/Canadian number typed into the search box, as E.164, or null.
 * Used to carry what was typed into the number field instead of making the
 * agent type it twice.
 */
export function nanpFromQuery(query: string): string | null {
  if (!looksLikePhoneNumber(query)) return null;
  const trimmed = query.trim();
  if (trimmed.startsWith('+') && !trimmed.startsWith('+1')) return null;
  const digits = trimmed.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10 ? `+1${national}` : null;
}

/** "+16025550142" → "(602) 555-0142". Anything else comes back unchanged. */
export function formatNanp(e164: string): string {
  if (!isNanpE164(e164)) return e164;
  const n = e164.slice(2);
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}
